/* @jsx h */
import { defineComponent, ref, onMounted, h, watch, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { api } from "@app/data/api";
import { ShiftDetails } from "@lemmonhq/types";
import { ShiftFilters } from "./components/ShiftFilters";
import { ShiftDetails as ShiftDetailsComponent } from "./components/ShiftDetails";
import {
  DateRange,
  DateRangeType,
  getDateRange,
  Loading,
} from "@app/vue/components/UIComponents";
import { useToast } from "@app/vue/providers/Toast";
import { useAuth } from "@app/vue/providers/Auth";
import { sharedDateRangeKey } from "@app/vue/composables/sharedDateRangeKey";
import {
  serializeFiltersToQuery,
  deserializeFiltersFromQuery,
} from "@lemmonhq/orders";
import { pp } from "@app/utils/clientBusiness";
import {
  ResponsiveTable,
  ResponsiveTableColumn,
  PageHeader,
  syncedRef,
} from "@lemmonhq/uikit";

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: "CHF",
    minimumFractionDigits: 2,
  }).format(amount);

const formatTime = (date: string) =>
  new Date(date).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

export const ShiftsReportPage = defineComponent({
  name: "ShiftsReportPage",
  setup() {
    const loading = ref(true);
    const loadingMore = ref(false);
    const shifts = ref<ShiftDetails[]>([]);
    const currentPage = ref(1);
    const totalPages = ref(1);
    const hasMore = ref(true);
    const { showToast } = useToast();
    const route = useRoute();
    const router = useRouter();
    const auth = useAuth();
    const containerRef = ref<HTMLElement>();

    // Determine default based on role: staff sees "today", others see "this_week"
    const isStaff = auth.user.value?.role === "staff";
    const DEFAULT_DATE_RANGE_TYPE = isStaff ? "today" : "this_week";

    // Shared with the other report pages and persisted, so all of them open on
    // the range the user last picked.
    const syncedDateRange = syncedRef(
      sharedDateRangeKey(
        auth.user.value?.restaurant_id,
        auth.user.value?.id,
      ),
      () => ({
        type: DEFAULT_DATE_RANGE_TYPE,
        ...getDateRange(DEFAULT_DATE_RANGE_TYPE),
      }),
    );

    /**
     * A link that names a range wins over the shared one — otherwise opening a
     * shared report URL would silently show a different period than it did for
     * the person who sent it.
     */
    const queryHasDateRange = () =>
      typeof route.query.dateRange === "string" ||
      (route.query.dateRangeType === "custom" &&
        typeof route.query.startDate === "string" &&
        typeof route.query.endDate === "string");

    /**
     * Staff may only look at today or yesterday. The picker hides the other
     * options, but a URL (`?dateRangeType=custom&…`) or the range shared with
     * the other report pages can still carry one in, so clamp it here too.
     * The API enforces the same window.
     */
    const STAFF_DATE_RANGE_TYPES: DateRangeType[] = ["today", "yesterday"];
    const allowedDateRange = (dateRange: DateRange): DateRange =>
      !isStaff || STAFF_DATE_RANGE_TYPES.includes(dateRange.type)
        ? dateRange
        : {
            type: DEFAULT_DATE_RANGE_TYPE,
            ...getDateRange(DEFAULT_DATE_RANGE_TYPE),
          };

    // Function to read filters from URL query parameters
    const getFiltersFromQuery = () => {
      const parsed = deserializeFiltersFromQuery(route.query, getDateRange, {
        numberFields: ["userId"],
        defaultDateRangeType: DEFAULT_DATE_RANGE_TYPE,
      }) as {
        userId?: number;
        dateRange: DateRange;
      };
      const dateRange = queryHasDateRange()
        ? parsed.dateRange
        : syncedDateRange.value;
      return { ...parsed, dateRange: allowedDateRange(dateRange) };
    };

    // Function to update URL query parameters
    const updateUrlQuery = (newFilters: {
      userId?: number;
      dateRange: DateRange;
    }) => {
      const query = serializeFiltersToQuery(newFilters, {
        defaultDateRangeType: DEFAULT_DATE_RANGE_TYPE,
      });
      console.log(`query`, query);
      router.replace({
        query: Object.keys(query).length > 0 ? query : {},
      });
    };

    const filters = ref<{ userId?: number; dateRange: DateRange }>(
      getFiltersFromQuery(),
    );

    // For ShiftDetails component
    const selectedShift = ref<ShiftDetails | null>(null);
    const isDetailsOpen = ref(false);

    const fetchShifts = async (reset = false) => {
      console.log(`fetchShifts = ${reset}`);
      if (reset) {
        loading.value = true;
        currentPage.value = 1;
        shifts.value = [];
        hasMore.value = true;
      } else {
        loadingMore.value = true;
      }

      try {
        const response = await api.getShifts({
          page: currentPage.value,
          limit: 10,
          dateRange: filters.value.dateRange as DateRange,
          userId: filters.value.userId,
        });

        if (reset) {
          shifts.value = response.data;
        } else {
          shifts.value = [...shifts.value, ...response.data];
        }

        totalPages.value = response.pagination.total_pages;
        hasMore.value = currentPage.value < response.pagination.total_pages;
      } catch (error) {
        console.error("Failed to fetch shifts:", error);
        showToast("Failed to fetch shifts", { type: "error" });
      } finally {
        loading.value = false;
        loadingMore.value = false;
      }
    };

    const loadMore = () => {
      if (!loadingMore.value && hasMore.value) {
        currentPage.value += 1;
        fetchShifts(false);
      }
    };

    const handleFilterChange = (newFilters: typeof filters.value) => {
      console.log("handleFilterChange", pp(newFilters));
      // Persist before the URL write: a range equal to the default produces an
      // empty query, so the route watch alone would not carry it anywhere.
      syncedDateRange.value = newFilters.dateRange;
      updateUrlQuery(newFilters);
      // Note: fetchShifts will be triggered by the watch on route.query
      // This prevents duplicate API calls
    };

    const handleShowDetails = (shift: ShiftDetails) => {
      selectedShift.value = shift;
      isDetailsOpen.value = true;
    };

    const handleScroll = () => {
      if (!containerRef.value) return;
      const { scrollTop, scrollHeight, clientHeight } = containerRef.value;
      const threshold = 200;

      if (
        scrollHeight - scrollTop - clientHeight < threshold &&
        hasMore.value &&
        !loadingMore.value
      ) {
        loadMore();
      }
    };

    // Watch for URL query changes (e.g., browser back/forward)
    watch(
      () => route.query,
      () => {
        const newFilters = getFiltersFromQuery();
        filters.value = newFilters;
        fetchShifts(true);
      },
    );

    onMounted(() => {
      fetchShifts(true);
    });

    const filterSummary = computed(() => {
      const { dateRange, userId } = filters.value;
      const parts: string[] = [dateRange.type.replace(/_/g, " ")];
      if (userId) {
        const colleague = auth.user.value?.colleagues?.find(
          (c: any) => c.id === userId,
        );
        if (colleague) parts.push(colleague.profile.name);
      }
      return parts.join(" · ");
    });

    const activeFilterCount = computed(() => {
      let n = 0;
      if (filters.value.dateRange.type !== DEFAULT_DATE_RANGE_TYPE) n++;
      if (filters.value.userId) n++;
      return n;
    });

    const columns: ResponsiveTableColumn[] = [
      // ── Desktop: Staff name ───────────────────────────────────────────────
      {
        key: "d-staff",
        label: "Staff",
        icon: "person",
        mobilePriority: "hidden",
        render: (shift: ShiftDetails) => (
          <span class="font-medium text-accent">
            {shift.user?.name || "Customer App"}
          </span>
        ),
      },
      // ── Desktop: Date & Time ──────────────────────────────────────────────
      {
        key: "d-datetime",
        label: "Date / Time",
        icon: "schedule",
        mobilePriority: "hidden",
        render: (shift: ShiftDetails) => (
          <div class="flex flex-col gap-0.5 text-sm">
            <span class="font-medium text-accent">
              {new Date(shift.scheduled_start).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
              })}
            </span>
            <span class="text-base-content/60">
              {formatTime(shift.scheduled_start)} →{" "}
              {formatTime(shift.scheduled_end)}
            </span>
            <span class="text-base-content/40 text-xs">
              opened {formatTime(shift.start)}
              {shift.end ? ` – closed ${formatTime(shift.end)}` : ""}
            </span>
          </div>
        ),
      },
      // ── Desktop: Payments ─────────────────────────────────────────────────
      {
        key: "d-payments",
        label: "Payments",
        icon: "payments",
        mobilePriority: "hidden",
        render: (shift: ShiftDetails) => {
          const b = shift.payment_breakdown;
          if (!b || b.total_count === 0)
            return (
              <span class="text-base-content/30 text-xs">No payments</span>
            );
          return (
            <div class="space-y-1">
              {b.cash_count > 0 && (
                <div class="flex items-center justify-between bg-emerald-200 border-2 border-emerald-600 rounded px-2 py-0.5">
                  <span class="text-xs text-emerald-900 font-medium flex items-center gap-1">
                    <span class="material-symbols-outlined text-xs text-emerald-800">
                      payments
                    </span>
                    Cash
                  </span>
                  <span class="text-xs font-bold text-emerald-900">
                    {formatCurrency(b.cash_amount)}
                  </span>
                </div>
              )}
              {b.card_count > 0 && (
                <div class="flex items-center justify-between bg-blue-200 border-2 border-blue-600 rounded px-2 py-0.5">
                  <span class="text-xs text-blue-900 font-medium flex items-center gap-1">
                    <span class="material-symbols-outlined text-xs text-blue-800">
                      credit_card
                    </span>
                    Card
                  </span>
                  <span class="text-xs font-bold text-blue-900">
                    {formatCurrency(b.card_amount)}
                  </span>
                </div>
              )}
              {b.twint_count > 0 && (
                <div class="flex items-center justify-between bg-violet-200 border-2 border-violet-600 rounded px-2 py-0.5">
                  <span class="text-xs text-violet-900 font-medium flex items-center gap-1">
                    <span class="material-symbols-outlined text-xs text-violet-800">
                      phone_android
                    </span>
                    TWINT
                  </span>
                  <span class="text-xs font-bold text-violet-900">
                    {formatCurrency(b.twint_amount)}
                  </span>
                </div>
              )}
              {b.online_count > 0 && (
                <div class="flex items-center justify-between bg-cyan-200 border-2 border-cyan-600 rounded px-2 py-0.5">
                  <span class="text-xs text-cyan-900 font-medium flex items-center gap-1">
                    <span class="material-symbols-outlined text-xs text-cyan-800">
                      language
                    </span>
                    Online
                  </span>
                  <span class="text-xs font-bold text-cyan-900">
                    {formatCurrency(b.online_amount)}
                  </span>
                </div>
              )}
              {b.manual_count > 0 && (
                <div class="flex items-center justify-between bg-orange-200 border-2 border-orange-600 rounded px-2 py-0.5">
                  <span class="text-xs text-orange-900 font-medium flex items-center gap-1">
                    <span class="material-symbols-outlined text-xs text-orange-800">
                      edit
                    </span>
                    Manual
                  </span>
                  <span class="text-xs font-bold text-orange-900">
                    {formatCurrency(b.manual_amount)}
                  </span>
                </div>
              )}
            </div>
          );
        },
      },
      // ── Desktop: Orders ───────────────────────────────────────────────────
      {
        key: "d-orders",
        label: "Orders",
        icon: "shopping_cart",
        mobilePriority: "hidden",
        render: (shift: ShiftDetails) => {
          const ob = shift.orders_breakdown;
          if (!ob || ob.total_orders === 0)
            return <span class="text-base-content/30 text-xs">No orders</span>;
          return (
            <div class="space-y-1">
              {ob.takeaway_count > 0 && (
                <div class="flex items-center justify-between bg-amber-200 border-2 border-amber-600 rounded px-2 py-0.5">
                  <span class="text-xs text-amber-900 font-medium flex items-center gap-1">
                    <span class="material-symbols-outlined text-xs text-amber-800">
                      takeout_dining
                    </span>
                    Takeaway
                  </span>
                  <span class="text-xs font-bold text-amber-900">
                    {ob.takeaway_count}
                  </span>
                </div>
              )}
              {ob.delivery_count > 0 && (
                <div class="flex items-center justify-between bg-green-200 border-2 border-green-600 rounded px-2 py-0.5">
                  <span class="text-xs text-green-900 font-medium flex items-center gap-1">
                    <span class="material-symbols-outlined text-xs text-green-800">
                      delivery_dining
                    </span>
                    Delivery
                  </span>
                  <span class="text-xs font-bold text-green-900">
                    {ob.delivery_count}
                  </span>
                </div>
              )}
              {ob.dine_in_count > 0 && (
                <div class="flex items-center justify-between bg-purple-200 border-2 border-purple-600 rounded px-2 py-0.5">
                  <span class="text-xs text-purple-900 font-medium flex items-center gap-1">
                    <span class="material-symbols-outlined text-xs text-purple-800">
                      restaurant
                    </span>
                    Dine-in
                  </span>
                  <span class="text-xs font-bold text-purple-900">
                    {ob.dine_in_count}
                  </span>
                </div>
              )}
            </div>
          );
        },
      },
      // ── Desktop: Total ────────────────────────────────────────────────────
      {
        key: "d-total",
        label: "Total",
        mobilePriority: "hidden",
        cellClass: "text-right tabular-nums",
        render: (shift: ShiftDetails) => {
          const b = shift.payment_breakdown;
          const ob = shift.orders_breakdown;
          const hasPayments = b && b.total_count > 0;
          const hasOrders = ob && ob.total_orders > 0;
          return (
            <div class="flex flex-col items-end gap-1">
              {hasPayments ? (
                <>
                  <span class="text-lg font-bold text-success">
                    {formatCurrency(b.total_amount)}
                  </span>
                  <span class="text-xs text-base-content/50">
                    {b.total_count} tx
                  </span>
                  {b.total_tips > 0 && (
                    <span class="text-xs text-accent">
                      +{formatCurrency(b.total_tips)} tips
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span class="text-sm text-base-content/30">CHF 0.00</span>
                  <span class="text-xs text-base-content/30">0 tx</span>
                </>
              )}
              {hasOrders && (
                <div class="border-t border-base-content/10 pt-1 mt-1 text-right">
                  <div class="text-sm font-medium text-accent">
                    {ob.total_orders} orders
                  </div>
                  <div class="text-xs text-base-content/50">
                    {formatCurrency(ob.total_revenue)}
                  </div>
                </div>
              )}
            </div>
          );
        },
      },
      // ── Desktop: Actions ──────────────────────────────────────────────────
      {
        key: "d-actions",
        label: "",
        mobilePriority: "hidden",
        headerClass: "w-px",
        cellClass: "text-right whitespace-nowrap",
        render: (shift: ShiftDetails) => (
          <button
            class="btn btn-sm btn-ghost"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              handleShowDetails(shift);
            }}
          >
            <span class="material-symbols-outlined text-base">open_in_new</span>
          </button>
        ),
      },

      // ═══════════════════════════════════════════════════════════════════════
      // MOBILE columns
      // ═══════════════════════════════════════════════════════════════════════

      // primary: staff name + total
      {
        key: "m-staff",
        label: "Staff",
        desktopHide: true,
        mobilePriority: "primary",
        render: (shift: ShiftDetails) => (
          <span class="font-semibold text-accent text-sm">
            {shift.user?.name || "Customer App"}
          </span>
        ),
      },
      {
        key: "m-total",
        label: "Total",
        desktopHide: true,
        mobilePriority: "primary",
        mobilePrimaryClass: "flex items-center justify-end gap-1.5",
        render: (shift: ShiftDetails) => {
          const b = shift.payment_breakdown;
          return b && b.total_count > 0 ? (
            <span class="font-bold text-success tabular-nums text-sm">
              {formatCurrency(b.total_amount)}
            </span>
          ) : (
            <span class="text-base-content/30 text-xs">CHF 0.00</span>
          );
        },
      },

      // secondary: date, tx count, orders count
      {
        key: "m-date",
        label: "Date",
        desktopHide: true,
        mobilePriority: "secondary",
        render: (shift: ShiftDetails) => (
          <span>
            {new Date(shift.scheduled_start).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
            })}{" "}
            {formatTime(shift.scheduled_start)}–
            {formatTime(shift.scheduled_end)}
          </span>
        ),
      },
      {
        key: "m-tx",
        label: "Transactions",
        desktopHide: true,
        mobilePriority: "secondary",
        render: (shift: ShiftDetails) => {
          const b = shift.payment_breakdown;
          return b && b.total_count > 0 ? (
            <span>{b.total_count} tx</span>
          ) : (
            <span class="text-base-content/30">—</span>
          );
        },
      },
      {
        key: "m-orders",
        label: "Orders",
        desktopHide: true,
        mobilePriority: "secondary",
        render: (shift: ShiftDetails) => {
          const ob = shift.orders_breakdown;
          return ob && ob.total_orders > 0 ? (
            <span>{ob.total_orders} orders</span>
          ) : (
            <span class="text-base-content/30">—</span>
          );
        },
      },
      {
        key: "m-opened",
        label: "Opened",
        desktopHide: true,
        mobilePriority: "secondary",
        render: (shift: ShiftDetails) => (
          <span>
            {formatTime(shift.start)}
            {shift.end ? ` – ${formatTime(shift.end)}` : ""}
          </span>
        ),
      },
    ];

    return () => (
      <div
        ref={containerRef}
        onScroll={handleScroll}
        class="w-full flex flex-col h-full overflow-y-auto"
      >
        <PageHeader
          title="Staff Shifts"
          filterSummary={filterSummary.value}
          activeFilterCount={activeFilterCount.value}
          filterSheetTitle="Filters"
        >
          {{
            default: ({
              closeMobileSheet,
            }: {
              closeMobileSheet: () => void;
            }) => (
              <ShiftFilters
                filters={filters.value}
                onFilterChange={handleFilterChange}
                users={auth.user.value?.colleagues || []}
                isStaff={isStaff}
                onApply={closeMobileSheet}
              />
            ),
          }}
        </PageHeader>

        <div class="flex-1 px-2 xl:p-4 pb-4">
          <ResponsiveTable
            columns={columns}
            rows={shifts.value}
            rowKey={(s: ShiftDetails) => s.id}
            onRowClick={handleShowDetails}
            loading={loading.value}
            loadingMore={loadingMore.value}
            emptyMessage="No shifts found — try adjusting your filters"
          />
          {!hasMore.value && shifts.value.length > 0 && (
            <div class="text-center py-4 text-sm opacity-60">
              All shifts loaded
            </div>
          )}
        </div>

        {selectedShift.value && (
          <ShiftDetailsComponent
            shift={selectedShift.value}
            isOpen={isDetailsOpen.value}
            onClose={() => {
              isDetailsOpen.value = false;
              selectedShift.value = null;
            }}
          />
        )}
      </div>
    );
  },
});
